import { Icon } from '@chakra-ui/react';
import {
  Box,
  Button,
  ChakraSelect,
  HStack,
  IconButtonWithTooltip,
  Switch,
  Tag,
  Td,
  Tooltip,
  Tr,
  Wrap,
  WrapItem,
} from '@elkaso-app/web-design';
import { mdiStar, mdiStarOutline } from '@mdi/js';
import MdiIcon from '@mdi/react';
import type { AttributeDictionaryType } from 'apis/product-attributes';
import { dictionaryLabelFor, dictionaryValues } from 'apis/product-attributes';
import { BrandSelect } from 'components/brand-select';
import { buildCanonicalOptions, toCanonicalValue } from 'pages/exclusive-deals/utils/canonical-options';
import { UOM_HELP } from 'pages/inquiry-processor/components/master-product-families/uom-field-meta';
import { useState } from 'react';
import { FiChevronDown, FiChevronUp, FiCopy, FiEdit2, FiGitMerge, FiTrash2 } from 'react-icons/fi';
import type { PackagingFields } from '../packaging';
import { SpecTagsInput } from '../spec-tags-input';
import type { EditableVariantRow } from './editable-variant-row.types';
import { PackagingCell } from './packaging-cell';
import { PackagingEditorRow } from './packaging-editor-row';
import { REQUIRED_MESSAGES } from './row-helpers';

// Max chips shown inline before collapsing the rest into a "+N" chip — the
// cell has to stay inside a table row, so it can't grow to fit every attribute.
const MAX_VISIBLE_CHIPS = 3;

// Compact per-row attributes summary: replaces the old clipping popover, which
// couldn't fit 8 attribute types without internal scroll. Click opens the
// full editor in AttributesDrawer (state owned by the grid, one instance).
//
// Empty rows used to render a bare '—', which read as "nothing to do here"
// rather than "click to add attributes" — replaced with a discoverable
// dashed-outline chip button. Populated rows keep the existing click-anywhere
// chip cluster, plus a trailing pencil glyph so editability stays visible
// once attributes are set (not just implied by cursor/hover).
export const AttributesChipsCell = ({
  attributes,
  dictionary,
  onClick,
}: {
  attributes: Record<string, string[]>;
  dictionary: AttributeDictionaryType[] | undefined;
  onClick: () => void;
}) => {
  // Flatten every value across every type into one chip cluster — a single
  // type holding multiple values renders one chip per value. Chips show the
  // dictionary LABEL ("Chump Off"), not the stored slug value ("chump-off");
  // legacy values missing from the dictionary fall back to the raw value.
  const values = Object.entries(attributes).flatMap(([type, typeValues]) =>
    typeValues.map(dictionaryLabelFor(dictionary, type))
  );
  const shown = values.slice(0, MAX_VISIBLE_CHIPS);
  const hiddenCount = values.length - shown.length;

  // Pressing Enter to activate this cell's button is a browser default
  // action, not a `preventDefault()`'d keydown — left unchecked it would also
  // bubble to the row's onKeyDown and trigger the "Enter appends a row"
  // behavior (see the Tr below). Stop it here instead.
  const stopEnterBubble = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') e.stopPropagation();
  };

  if (values.length === 0) {
    return (
      <Button
        size='xs'
        variant='outline'
        borderStyle='dashed'
        color='gray.500'
        fontWeight='normal'
        onClick={onClick}
        onKeyDown={stopEnterBubble}
        aria-label='add-variant-attributes-btn'>
        + Attributes
      </Button>
    );
  }

  return (
    <Box
      as='button'
      type='button'
      onClick={onClick}
      onKeyDown={stopEnterBubble}
      textAlign='left'
      cursor='pointer'
      _hover={{ opacity: 0.75 }}
      aria-label='edit-variant-attributes-btn'>
      <HStack spacing='1' align='center'>
        <Wrap spacing='1'>
          {shown.map((value, i) => (
            <WrapItem key={i}>
              <Tag size='sm'>{value}</Tag>
            </WrapItem>
          ))}
          {hiddenCount > 0 && (
            <WrapItem>
              <Tag size='sm' variant='outline'>
                +{hiddenCount}
              </Tag>
            </WrapItem>
          )}
        </Wrap>
        <Icon as={FiEdit2} boxSize='3' color='gray.400' />
      </HStack>
    </Box>
  );
};

export interface EditableVariantRowViewProps {
  row: EditableVariantRow;
  index: number;
  messages: string[]; // this row's validation messages, from the hook's errorsByClientId
  // Saved row whose UOM ladder fields differ from pristine — the packaging
  // expander shows its recorded-prices note only for a real ladder change.
  isPackagingDirty?: boolean;
  columnCount: number; // total header column count, for the packaging expander's colSpan
  isLastRow: boolean;
  dictionary: AttributeDictionaryType[] | undefined;
  uomOptions: readonly string[];
  brandOptions: readonly string[];
  onChange: (clientId: string, patch: Partial<EditableVariantRow>) => void;
  onInsert: (index: number, position: 'above' | 'below') => void;
  onRemove: (index: number) => void;
  onDeleteVariant: (index: number) => void;
  onMergeVariant: (index: number) => void;
  // False when this family has only one saved variant — there is nothing to merge into.
  canMergeVariant: boolean;
  onDuplicate: (index: number) => void;
  onEnterAppend: () => void;
  onOpenAttributesDrawer: (clientId: string) => void;
  // One-time post-merge highlight (see variants-grid.tsx) — purely visual,
  // never part of the row model, so it can't affect dirty/validation.
  isHighlighted?: boolean;
  highlightSourceName?: string;
  // Curated "top product" star flag — lives in the grid's topFlags map, not the row
  // model (see variants-grid.tsx). Always false for unsaved rows (row.id === undefined).
  isTopProduct?: boolean;
  // True while this row's toggle PUT is in flight — disables the star button so a
  // double-click can't fire a second concurrent request (see variants-grid.tsx).
  isTopProductPending?: boolean;
  onToggleTopProduct: (id: number) => void;
}

type VariantRowActionsProps = Pick<
  EditableVariantRowViewProps,
  | 'row'
  | 'index'
  | 'canMergeVariant'
  | 'isTopProduct'
  | 'isTopProductPending'
  | 'onToggleTopProduct'
  | 'onInsert'
  | 'onRemove'
  | 'onDuplicate'
  | 'onMergeVariant'
  | 'onDeleteVariant'
> & {
  // 'table' keeps the desktop grid's compact xs buttons; 'card' bumps them to
  // >=42px tap targets for the mobile variant card (variant-card.tsx).
  display?: 'table' | 'card';
};

// The row's up-to-6 action icon buttons, shared verbatim between the desktop
// table row (last <Td>) and the mobile card's actions row — one implementation
// so the delete/remove split, merge gating and the top-product star behave
// identically on both layouts.
export const VariantRowActions = ({
  row,
  index,
  canMergeVariant,
  isTopProduct,
  isTopProductPending,
  onToggleTopProduct,
  onInsert,
  onRemove,
  onDuplicate,
  onMergeVariant,
  onDeleteVariant,
  display = 'table',
}: VariantRowActionsProps) => {
  const canRemove = row.id === undefined; // only new rows are removable
  const btnProps: { size: 'xs' | 'sm'; minW?: string; minH?: string } =
    display === 'card' ? { size: 'sm', minW: '42px', minH: '42px' } : { size: 'xs' };

  return (
    <>
      {row.id !== undefined && (
        <IconButtonWithTooltip
          {...btnProps}
          variant='ghost'
          isLoading={isTopProductPending}
          isDisabled={isTopProductPending}
          label={isTopProduct ? 'Remove from top products' : 'Mark as top product'}
          aria-label='toggle-top-product'
          Icon={
            <MdiIcon
              path={isTopProduct ? mdiStar : mdiStarOutline}
              size={0.75}
              color={isTopProduct ? '#B26B00' : '#757575'}
            />
          }
          onClick={() => onToggleTopProduct(row.id as number)}
        />
      )}
      <IconButtonWithTooltip
        {...btnProps}
        variant='ghost'
        label='Add row above'
        aria-label='add-variant-row-above'
        Icon={<Icon as={FiChevronUp} />}
        onClick={() => onInsert(index, 'above')}
      />
      <IconButtonWithTooltip
        {...btnProps}
        variant='ghost'
        label='Add row below'
        aria-label='add-variant-row-below'
        Icon={<Icon as={FiChevronDown} />}
        onClick={() => onInsert(index, 'below')}
      />
      <IconButtonWithTooltip
        {...btnProps}
        variant='ghost'
        label='Duplicate row'
        aria-label='duplicate-variant-row'
        Icon={<Icon as={FiCopy} />}
        onClick={() => onDuplicate(index)}
      />
      {!canRemove && canMergeVariant && (
        <IconButtonWithTooltip
          {...btnProps}
          variant='ghost'
          label='Merge into…'
          aria-label='merge-variant-row'
          Icon={<Icon as={FiGitMerge} />}
          onClick={() => onMergeVariant(index)}
        />
      )}
      {canRemove ? (
        <IconButtonWithTooltip
          {...btnProps}
          variant='ghost'
          colorScheme='red'
          label='Remove row'
          aria-label='remove-variant-row'
          Icon={<Icon as={FiTrash2} />}
          onClick={() => onRemove(index)}
        />
      ) : (
        <IconButtonWithTooltip
          {...btnProps}
          variant='ghost'
          colorScheme='red'
          label='Delete variant'
          aria-label='delete-variant-row'
          Icon={<Icon as={FiTrash2} />}
          onClick={() => onDeleteVariant(index)}
        />
      )}
    </>
  );
};

// Derived, per-field view state shared verbatim between the desktop table row
// and the mobile card (variant-card.tsx): the substring-matched per-field
// validation messages and the canonical select options/values for
// State/Origin/Brand, plus the composed packaging fields. Pure derivation —
// no hooks — so both layouts stay in exact behavioral parity.
//
// The state DTO enforces @IsEnum(ProductState) server-side, so State must be a canonical
// select rather than free text (unlike Brand/Origin/Base Unit/Spec, still plain strings).
// Existing variants' `state` comes back canonical already; toCanonicalValue is a defensive
// normalize so legacy/mismatched-case data still pre-selects instead of falling through to
// the "(legacy)" option. Mirrors VariantFormModal's selects. State/origin canon comes from
// the (category-scoped) attribute dictionary; brand canon from the product-brands dictionary.
export const deriveVariantRowView = (
  row: EditableVariantRow,
  messages: string[],
  dictionary: AttributeDictionaryType[] | undefined,
  brandOptions: readonly string[]
) => {
  // Per-cell tooltip messages: `messages` is a flat list of this row's validation
  // errors (validateRows' generic strings), matched back to the field(s) they came
  // from by substring so each cell only surfaces what's relevant to it.
  const stateMessages = messages.filter((m) => m.includes(REQUIRED_MESSAGES.state));
  const brandMessages = messages.filter((m) => m.includes(REQUIRED_MESSAGES.brand));
  const packagingMessages = messages.filter(
    (m) =>
      m.includes(UOM_HELP.baseUnit.label) ||
      m.includes(UOM_HELP.netContent.label) ||
      m.includes(UOM_HELP.packSize.label)
  );

  const stateCanon = dictionaryValues(dictionary, 'state');
  const canonicalState = toCanonicalValue(stateCanon, row.state);
  const stateOptions = buildCanonicalOptions(stateCanon, canonicalState, dictionaryLabelFor(dictionary, 'state'));
  const selectedState = stateOptions.find((o) => o.value === canonicalState) ?? null;

  // Origin and Base Unit are also canonical-option selects server-side (free text stored, but
  // VariantFormModal's fromProduct/submit treat them via the dictionary/the UOM options endpoint),
  // so the grid mirrors that here.
  const originCanon = dictionaryValues(dictionary, 'origin');
  const canonicalOrigin = toCanonicalValue(originCanon, row.origin);
  const originOptions = buildCanonicalOptions(originCanon, canonicalOrigin, dictionaryLabelFor(dictionary, 'origin'));
  const selectedOrigin = originOptions.find((o) => o.value === canonicalOrigin) ?? null;

  // Brand is a canonical select over the product-brands dictionary — like State it's required,
  // so no isClearable; legacy values not in the dictionary surface as a "(legacy)" option.
  const canonicalBrand = toCanonicalValue(brandOptions, row.brand);
  const brandSelectOptions = buildCanonicalOptions(brandOptions, canonicalBrand);
  const selectedBrand = brandSelectOptions.find((o) => o.value === canonicalBrand) ?? null;

  const packagingFields: PackagingFields = {
    baseUnit: row.baseUnit,
    itemUnit: row.itemUnit,
    netContent: row.netContent,
    packUnit: row.packUnit,
    packSize: row.packSize,
  };

  return {
    stateMessages,
    brandMessages,
    packagingMessages,
    stateOptions,
    selectedState,
    originOptions,
    selectedOrigin,
    brandSelectOptions,
    selectedBrand,
    packagingFields,
  };
};

export const EditableVariantRowView = ({
  row,
  index,
  messages,
  isPackagingDirty,
  columnCount,
  isLastRow,
  dictionary,
  uomOptions,
  brandOptions,
  onChange,
  onInsert,
  onRemove,
  onDeleteVariant,
  onMergeVariant,
  canMergeVariant,
  onDuplicate,
  onEnterAppend,
  onOpenAttributesDrawer,
  isHighlighted,
  highlightSourceName,
  isTopProduct,
  isTopProductPending,
  onToggleTopProduct,
}: EditableVariantRowViewProps) => {
  const invalid = messages.length > 0;
  const [isPackagingExpanded, setIsPackagingExpanded] = useState(false);

  const {
    stateMessages,
    brandMessages,
    packagingMessages,
    stateOptions,
    selectedState,
    originOptions,
    selectedOrigin,
    brandSelectOptions,
    selectedBrand,
    packagingFields,
  } = deriveVariantRowView(row, messages, dictionary, brandOptions);

  return (
    <>
      <Tr
        bg={invalid ? 'red.50' : isHighlighted ? 'yellow.50' : undefined}
        onKeyDown={(e) => {
          // react-select calls preventDefault() on the Enter that confirms a ChakraSelect
          // option (State/Origin), but doesn't stopPropagation — without this guard that same
          // keystroke bubbles up here and also appends a row.
          if (isLastRow && e.key === 'Enter' && !e.defaultPrevented) onEnterAppend();
        }}>
        <Tooltip label={brandMessages.join(', ')} isDisabled={!brandMessages.length} hasArrow placement='top'>
          <Td>
            <HStack spacing='1'>
              <Box w='140px'>
                <BrandSelect
                  size='sm'
                  options={brandSelectOptions}
                  value={selectedBrand}
                  isInvalid={brandMessages.length > 0}
                  placeholder='Brand'
                  onBrandChange={(brand) => onChange(row.clientId, { brand })}
                />
              </Box>
              {isHighlighted && (
                <Tooltip label={`Merged from '${highlightSourceName}'`} hasArrow placement='top'>
                  <Tag size='sm' colorScheme='yellow' flexShrink={0}>
                    Merged
                  </Tag>
                </Tooltip>
              )}
            </HStack>
          </Td>
        </Tooltip>
        <Td>
          <Box w='140px'>
            <ChakraSelect
              size='sm'
              options={originOptions}
              value={selectedOrigin}
              placeholder='Origin'
              isClearable
              menuPortalTarget={document.body}
              menuPosition='fixed'
              styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              onChange={(opt: any) => onChange(row.clientId, { origin: opt?.value ?? '' })}
            />
          </Box>
        </Td>
        <Tooltip label={stateMessages.join(', ')} isDisabled={!stateMessages.length} hasArrow placement='top'>
          <Td>
            <Box w='110px'>
              <ChakraSelect
                size='sm'
                options={stateOptions}
                value={selectedState}
                isInvalid={stateMessages.length > 0}
                placeholder='State'
                menuPortalTarget={document.body}
                menuPosition='fixed'
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                onChange={(opt: any) => onChange(row.clientId, { state: opt?.value ?? '' })}
              />
            </Box>
          </Td>
        </Tooltip>
        <Tooltip label={packagingMessages.join(', ')} isDisabled={!packagingMessages.length} hasArrow placement='top'>
          <Td>
            <PackagingCell
              fields={packagingFields}
              isExpanded={isPackagingExpanded}
              onToggle={() => setIsPackagingExpanded((v) => !v)}
            />
          </Td>
        </Tooltip>
        <Td>
          <Box maxW='220px'>
            <SpecTagsInput size='sm' value={row.spec} onChange={(spec) => onChange(row.clientId, { spec })} />
          </Box>
        </Td>
        <Td>
          <AttributesChipsCell
            attributes={row.attributes}
            dictionary={dictionary}
            onClick={() => onOpenAttributesDrawer(row.clientId)}
          />
        </Td>
        <Td textAlign='center'>
          <Switch
            size='sm'
            isChecked={row.isActive}
            onChange={(e) => onChange(row.clientId, { isActive: e.target.checked })}
          />
        </Td>
        <Td whiteSpace='nowrap'>
          <VariantRowActions
            row={row}
            index={index}
            canMergeVariant={canMergeVariant}
            isTopProduct={isTopProduct}
            isTopProductPending={isTopProductPending}
            onToggleTopProduct={onToggleTopProduct}
            onInsert={onInsert}
            onRemove={onRemove}
            onDuplicate={onDuplicate}
            onMergeVariant={onMergeVariant}
            onDeleteVariant={onDeleteVariant}
          />
        </Td>
      </Tr>
      {isPackagingExpanded && (
        <PackagingEditorRow
          colSpan={columnCount}
          fields={packagingFields}
          uomOptions={uomOptions}
          onChange={(patch) => onChange(row.clientId, patch)}
          variantId={row.id}
          isPackagingDirty={isPackagingDirty}
        />
      )}
    </>
  );
};
